<script>
import {
    eventGroupState
} from '@app/store/eventGroupStore.js';
import {
    projectState
} from '@app/store/projectStore.js';
import {
    highlightedEventState
} from '@app/store/highlightedEventStore.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    lockState
} from '@app/store/lockStore.js';
import {
    environment
} from '@app/store/envStore.js';
import {
    historyState
} from '@app/store/historyStore.js';
import {
    editorState
} from "@app/store/editorStore.js";
import {
    modalState
} from '@app/store/modalStore.js';
import debounce from "just-debounce-it";
import {
    Circle
} from 'svelte-loading-spinners';
import { clickOutside } from 'svelte-use-click-outside';
import {decode} from 'html-entities';
import AdQuillEditor from './AdQuillEditor.svelte';
import TranscriptEditorPreview from './TranscriptEditorPreview.svelte';
import TcInput from './inputs/TcInput.svelte';
import SliderInput from './inputs/SliderInput.svelte';
import RealTimeErrors from './RealTimeErrors.svelte';
import EventNotes from './EventNotes.svelte';
import EventApproval from './EventApproval.svelte';
import ForcedSubtitleEvent from './ForcedSubtitleEvent.svelte';
import EventMetrics from './EventMetrics.svelte';
import EventSpeakers from './EventSpeakers.svelte';
import EventRecipients from './EventRecipients.svelte';
import EventTags from './EventTags.svelte';

import convertToPlainText from '@app/external/cc-lib/functions/quill/convertToPlainText.js';
import convertToHtml from '@app/external/cc-lib/functions/quill/convertToHtml.js';
import sccLib from '@app/external/cc-lib/dist/dict/608.js';
import insertEv from '@app/external/cc-lib/dist/functions/eventGroups/insertEvent.js';

/* Firebase */
import firebase from '@app/configs/firebase.js';
import 'firebase/compat/functions';

export let eventIndex;
let rendering = false;
let rendered = false;
let showAiTools = false;
/* Event Metrics */
let wpm = 0,
    cps = 0,
    duration = 100,
    totalWords = 0,
    lineInfo = 0,
    totalChars = 0,
    overlaps, 
    illegalChars, 
    errorMsg = "";

function insertEvent() {
    try {
        if (!$eventGroupState[$projectState.selected]) {
            return;
        }

        let eventOptions;

        if ($eventGroupState[$projectState.selected].events.length > 0) {
            try {
                let eventTemplate = $eventGroupState[$projectState.selected].selected.length > 0 ? $eventGroupState[$projectState.selected].events[$eventGroupState[$projectState.selected].selected[0]] : $eventGroupState[$projectState.selected].events[$eventGroupState[$projectState.selected].events.length - 1];

                eventOptions = {
                    alignment: eventTemplate.alignment,
                    xPos: eventTemplate.xPos,
                    yPos: eventTemplate.yPos,
                    xOffset: eventTemplate.xOffset,
                    yOffset: eventTemplate.yOffset,
                    vertical: eventTemplate.vertical,
                    voice: eventTemplate.voice,
                    speakingStyle: eventTemplate.speakingStyle,
                    style: eventTemplate.style,
                    rate: eventTemplate.rate,
                    start: $editorState.insertStartOnInsert ? player.currentTime : false,
                    end: $editorState.insertStartOnInsert ? player.currentTime + $eventGroupState[$projectState.selected].minDuration : false,
                }
            } catch (err) {
                console.log(err);
            }
        } else {
            eventOptions = {
                alignment: "left",
                voice: $eventGroupState[$projectState.selected].ad.voice
            }
        }

        $eventGroupState[$projectState.selected] = insertEv($eventGroupState[$projectState.selected], eventOptions, eventIndex + 1);

        setTimeout(function() {
            $eventGroupState[$projectState.selected].selected = [eventIndex + 1]
        }, 50);

        /* Store in History */
        historyState.insert({
            name: "insert event", //action name
            eventGroup: $projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify($eventGroupState)
            }]
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

function selectEvent(clickEvent, index) {
    try {
        if (clickEvent.ctrlKey || clickEvent.metaKey) {
            if ($eventGroupState[$projectState.selected].selected.indexOf(index) > -1) {
                $eventGroupState[$projectState.selected].selected = $eventGroupState[$projectState.selected].selected.filter(event => {
                    return event !== index;
                });
            } else {
                $eventGroupState[$projectState.selected].selected = [...$eventGroupState[$projectState.selected].selected, index];
            }
        } else if (clickEvent.shiftKey && $eventGroupState[$projectState.selected].selected.length > 0) {
            let indexOfLastSelectedEvent = $eventGroupState[$projectState.selected].selected[0]
            if (index > indexOfLastSelectedEvent) {
                while (indexOfLastSelectedEvent < index) {
                    $eventGroupState[$projectState.selected].selected = [...$eventGroupState[$projectState.selected].selected, indexOfLastSelectedEvent + 1];
                    indexOfLastSelectedEvent++
                }
            } else {
                while (indexOfLastSelectedEvent > index) {
                    $eventGroupState[$projectState.selected].selected = [...$eventGroupState[$projectState.selected].selected, indexOfLastSelectedEvent - 1];
                    indexOfLastSelectedEvent--;
                }
            }
        } else {
            $eventGroupState[$projectState.selected].selected = [index];
            if ($lockState.video && !isNaN($eventGroupState[$projectState.selected].events[index].start)) {
                player.currentTime = $eventGroupState[$projectState.selected].events[index].start;
            }
        }
    } catch(err){
        console.log("There was an error selecting the event. "+ err.message);
        console.log(err);
    }
}

const updateEventMetrics = debounce(async () => {
    if (!$eventGroupState[$projectState.selected].events[eventIndex]){
        return;
    }

    let plainText = convertToPlainText($eventGroupState[$projectState.selected].events[eventIndex].text);
    /* Duration */
    duration = ($eventGroupState[$projectState.selected].events[eventIndex].end - $eventGroupState[$projectState.selected].events[eventIndex].start).toFixed(2);
    /* Line Info */
    lineInfo = []
    plainText.split("\n").forEach(line => {
        lineInfo.push(decode(line).length);
    });
    /* Total Characters */
    totalChars = lineInfo.reduce((partialSum, a) => partialSum + a, 0);
    /* Total Words */
    totalWords = plainText.split(/\s/gm).length;
    /* wpm */
    wpm = ((totalWords / duration) * 60).toFixed(2);
    /* cps */
    cps = (totalChars / duration).toFixed(2);
    /* Overlaps */
    overlaps = $eventGroupState[$projectState.selected].events.find((event, i, events) => {
        return eventIndex !== i && ((event.start > events[eventIndex].start && event.start < events[eventIndex].end) || (event.end < events[eventIndex].end && event.end > events[eventIndex].start));
    });

    /* Illegal Characters */
    illegalChars = plainText.split("").find(char => {
        return sccLib.valid.indexOf(char) === -1;
    });

    updateErrorMessage();
}, 50);

async function updateErrorMessage() {
    let messages = [];

    if (overlaps){
        if ($eventGroupState[$projectState.selected].overlap && overlaps) {
            messages.push("Event overlap detected.");
        }
    } 

    if ($eventGroupState[$projectState.selected].illegalChars && illegalChars) {
        messages.push(`Illegal non-608 compliant character found ${illegalChars}`);
    }

    if (wpm > $eventGroupState[$projectState.selected].maxWpm) {
        messages.push(`Words Per Minute (${wpm}) exceeds ${$eventGroupState[$projectState.selected].maxWpm}`);
    }

    if (cps > $eventGroupState[$projectState.selected].maxCps) {
        messages.push(`Characters Per Second (${cps}) exceeds ${$eventGroupState[$projectState.selected].maxCps}`);
    }

    if (duration > $eventGroupState[$projectState.selected].maxDuration || duration < $eventGroupState[$projectState.selected].minDuration) {
        messages.push(`Duration of Event (${duration}s) must be between ${$eventGroupState[$projectState.selected].minDuration} and ${$eventGroupState[$projectState.selected].maxDuration} seconds.`);
    }

    if (lineInfo && lineInfo.length > $eventGroupState[$projectState.selected].maxLines) {
        messages.push(`The number of lines (${lineInfo.length}) exceeds ${$eventGroupState[$projectState.selected].maxLines}`);
    }

    if (lineInfo && lineInfo.some(el => el > $eventGroupState[$projectState.selected].maxChars)) {
        messages.push(`The number of characters exceeds ${$eventGroupState[$projectState.selected].maxChars}`);
    }

    errorMsg = messages.join("\n");
}

async function trimToDuration() {
    $eventGroupState[$projectState.selected].events[eventIndex].end = $eventGroupState[$projectState.selected].events[eventIndex].start + $eventGroupState[$projectState.selected].events[eventIndex].audioFileDuration+0.5;
}

async function previewAudio() {
    if (window.previewPlayTimeout){
        clearTimeout(window.previewPlayTimeout);
    }

    if (player) {
        if ($eventGroupState[$projectState.selected].events[eventIndex].start >= 0 && $eventGroupState[$projectState.selected].events[eventIndex].end >= 0){
            player.playbackRate = 1;            
            player.currentTime = Math.max($eventGroupState[$projectState.selected].events[eventIndex].start - 0.5, 0);
            if (!player.state.playing){
                await player.play();
            }

            window.previewPlayTimeout = setTimeout(() => {
                player.pause()
            }, 1000*(1 + $eventGroupState[$projectState.selected].events[eventIndex].end - $eventGroupState[$projectState.selected].events[eventIndex].start));
        } else {
            toast.push("In and Out times of Event not set.", {classes: ["toast-danger"]});
        }        
    }
}

function toggleAiToolsMenu(){
    setTimeout(() => {
        showAiTools = !showAiTools;
    }, 0);
}

function closeAiToolsMenu(){
    if (showAiTools){
        showAiTools = false;
    }    
}

async function aiRephrase(rate){
    try {
        rendering = true;
        closeAiToolsMenu();

        let text = convertToPlainText($eventGroupState[$projectState.selected].events[eventIndex].text, " ");
        let duration, wpm;

        if (!text) {
            return;
        }

        if (!isNaN($eventGroupState[$projectState.selected].events[eventIndex].end) &&  !isNaN($eventGroupState[$projectState.selected].events[eventIndex].start)){
            duration = $eventGroupState[$projectState.selected].events[eventIndex].end - $eventGroupState[$projectState.selected].events[eventIndex].start;
            wpm = text.split(" ").length/(duration/60);
        }

        /* console.log("Duration of event: "+ duration);
        console.log("WPM: "+ wpm);
        console.log("Event Text: "+ text); */

        let res = await firebase.functions().httpsCallable('v8AdScriptAssistant')({
            text: text,
            rate : rate,
            wpm : wpm,
            numberOfWords : text.split(" ").length,
            duration : duration,
            groupId: $eventGroupState[$projectState.selected].id,
            eventId: $eventGroupState[$projectState.selected].events[eventIndex].id,
        });

        // console.log(res.data);

        $eventGroupState[$projectState.selected].events[eventIndex].text = convertToHtml(res.data.text);
        
        try {
            player.pause();
        } catch(err){
            console.log(err.message);
        }

        $eventGroupState[$projectState.selected].selected = [];   

        setTimeout(() => {
            console.log("Setting rendered to false in AI Rephrase");
            $eventGroupState[$projectState.selected].events[eventIndex].rendered = false;
        }, 50);       
        
        /* Store in History */
        historyState.insert({
            name: "rephrase text", //action name
            eventGroup: $projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify($eventGroupState)
            }]
        });
    } catch(err){
        console.log(err, err.message);
        toast.push("Failed to rephrase text. "+err.message, {classes: ["toast-danger"]});
    } finally {
        rendering = false;
    }
}

function renderAudio(){
    $eventGroupState[$projectState.selected].selected = [eventIndex];
    modalState.showModal("renderAudio");
    rendered = true;
}

$: if ($eventGroupState[$projectState.selected].events[eventIndex]) {
    try {
        updateEventMetrics();
    } catch (err) {
        console.log(err, err.message);
    }
}
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<form
    id="audioDescriptionForm"
    on:click="{clickEvent => selectEvent(clickEvent, eventIndex)}"
    class="rounded-0 p-3 eventItem {$eventGroupState[$projectState.selected] && $eventGroupState[$projectState.selected].selected.indexOf(eventIndex) > -1 ? 'border border-warning bg-warning bg-opacity-25' : ''}" style="{$highlightedEventState.enabled && $highlightedEventState.events.indexOf($eventGroupState[$projectState.selected].events[eventIndex].id) === -1 ? 'opacity:0.2' : ''}">
    <div class="row">
        <!-- Event Metrics -->
        <div class="col-2">
            <div class="btn-group float-end" role="group">
                <RealTimeErrors
                    errorMsg="{errorMsg}"
                    ></RealTimeErrors>
                <ForcedSubtitleEvent event={$eventGroupState[$projectState.selected].events[eventIndex]}></ForcedSubtitleEvent>
                <EventApproval event={$eventGroupState[$projectState.selected].events[eventIndex]}></EventApproval>
                <EventNotes event={$eventGroupState[$projectState.selected].events[eventIndex]}></EventNotes>
            </div>

            <p class="text-dark mb-1 fw-bold" title="Event ID">{eventIndex+1}</p>
            <p class="text-truncate text-custom-small text-dark fst-italic m-0" title="{$eventGroupState[$projectState.selected].events[eventIndex].voice.name}">{$eventGroupState[$projectState.selected].events[eventIndex].voice.name}</p>
            <select class="text-custom-medium speakerStyleSelect mb-2 w-100" bind:value="{$eventGroupState[$projectState.selected].events[eventIndex].speakingStyle}" on:change="{() => {$eventGroupState[$projectState.selected].events[eventIndex].rendered = false}}">
                +
                {#each $eventGroupState[$projectState.selected].events[eventIndex].voice.styles as styleOption}
                <option>{styleOption}</option>
                {/each}
            </select>

            <EventMetrics wpm="{wpm}" cps="{cps}" duration="{duration}" totalWords="{totalWords}" lineInfo="{lineInfo}" totalChars="{totalChars}" eventGroupSettings={{
                maxChars : $eventGroupState[$projectState.selected].maxChars,
                maxLines : $eventGroupState[$projectState.selected].maxLines,
                maxCps : $eventGroupState[$projectState.selected].maxCps,
                maxWpm : $eventGroupState[$projectState.selected].maxWpm,
                minDuration : $eventGroupState[$projectState.selected].minDuration,
                maxDuration : $eventGroupState[$projectState.selected].maxDuration
            }}></EventMetrics>

            {#if rendering}
                <Circle size="30" color="#1eb4b2" unit="px" duration="1s"></Circle>
            {/if}
        </div>
        <!-- Event Timecode -->
        <div class="col-auto">
            <div class="input-group input-group-sm mb-1">
                <TcInput eventIndex="{eventIndex}" tcType="{'start'}"></TcInput>
            </div>
            <div class="input-group input-group-sm mb-2">
                <TcInput eventIndex="{eventIndex}" tcType="{'end'}"></TcInput>
            </div>
            {#if $eventGroupState[$projectState.selected].events[eventIndex].voice.provider === "ElevenLabs"}
            <SliderInput eventIndex="{eventIndex}" property="voiceStability" label="Stability" minValue="30" maxValue="100" stepValue="1" unit="%"></SliderInput>
            <SliderInput eventIndex="{eventIndex}" property="voiceSimilarity" label="Similarity" minValue="30" maxValue="100" stepValue="1" unit="%"></SliderInput>
            {:else}
                <SliderInput eventIndex="{eventIndex}" property="rate" label="Rate" minValue="0.5" maxValue="1.5" stepValue="0.01" unit="x"></SliderInput>
            {/if}

            <div class="btn-group btn-group-sm w-100 border border-secondary" role="group">
                <!-- Render Button -->
                {#if $environment.online}
                    <button
                        id="renderBtn-{eventIndex}"
                        type="button"
                        class="btn {!$eventGroupState[$projectState.selected]?.events[eventIndex]?.rendered ? 'btn-primary text-white glowing-button' : 'btn-light text-dark'}"
                        disabled="{rendering}"
                        on:click={() => renderAudio()}
                        title="Render virtual audio (text-to-speech)"
                        aria-label="Render this event">
                        <i class="bi bi-bootstrap-reboot"></i>
                    </button>
                {/if}
                <!-- Preview Button -->
                <button
                    id="previewBtn-{eventIndex}"
                    type="button"
                    class="btn btn-light text-dark"
                    disabled="{rendering || isNaN($eventGroupState[$projectState.selected]?.events[eventIndex]?.start)}"
                    on:click|preventDefault|stopPropagation="{previewAudio}"
                    title="Preview event audio"
                    aria-label="Preview event audio">
                    <i class="bi bi-play-fill"></i>
                </button>
                <!-- Trim Button -->
                {#if $environment.online}
                <button
                    id="trimBtn-{eventIndex}"
                    type="button"
                    title="Trim or extend the event duration based on the virtual audio duration."
                    aria-label="Trim event duration"
                    class="btn btn-light text-dark"
                    disabled="{rendering || isNaN($eventGroupState[$projectState?.selected]?.events[eventIndex]?.start) || $eventGroupState[$projectState.selected]?.events[eventIndex]?.audioFile.length === 0}"
                    on:click="{trimToDuration}">
                    <i class="bi bi-scissors"></i>
                </button>
                <!-- Open AI -->
                <div title="Ai Authoring Tools" class="btn-group btn-group-sm" role="group">
                    <button
                        id="aiToolsBtn-{eventIndex}"
                        type="button"
                        class="btn btn-light dropdown-toggle {showAiTools ? 'show' : ''}"
                        on:click={() => toggleAiToolsMenu()}
                        aria-haspopup="true"
                        aria-expanded="{showAiTools}"
                        aria-label="Show AI Tools menu">
                        <i class="bi bi-cpu"></i>
                    </button>
                    <ul class="dropdown-menu {showAiTools ? 'show' : ''}" style={showAiTools ? 'position: absolute; inset: 0px auto auto 0px; margin: 0px; transform: translate(0px, 33px);' : ''} use:clickOutside={closeAiToolsMenu}>
                        <li><a class="dropdown-item" href="#!/" on:click={() => aiRephrase("shorten")}>Rephrase - Shorten</a></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() => aiRephrase("expand")}>Rephrase - Expand</a></li>
                        <li><hr class="dropdown-divider"></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() =>aiRephrase(150)}>Rephrase to 150 WPM</a></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() =>aiRephrase(175)}>Rephrase to 175 WPM</a></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() =>aiRephrase(200)}>Rephrase to 200 WPM</a></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() =>aiRephrase(225)}>Rephrase to 225 WPM</a></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() =>aiRephrase(250)}>Rephrase to 250 WPM</a></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() =>aiRephrase(275)}>Rephrase to 275 WPM</a></li>
                        <li><a class="dropdown-item" href="#!/" on:click={() =>aiRephrase(300)}>Rephrase to 300 WPM</a></li>
                    </ul>
                </div>
                {/if}
            </div>
            {#if $eventGroupState[$projectState.selected]?.events[eventIndex]?.audioFileDuration && duration < $eventGroupState[$projectState?.selected]?.events[eventIndex]?.audioFileDuration-0.5}
            <span class="badge bg-danger p-1 mt-2" title="We believe the number of words exceeds the duration of the event. Try trimming the event to fix this error."><i class="bi bi-exclamation-lg"></i> DURATION</span>
            {/if}
        </div>
        <!-- Event Text -->
        <div class="col">
            {#if $eventGroupState[$projectState.selected].selected[0] === eventIndex }
            <AdQuillEditor eventIndex="{eventIndex}" rendered="{rendered}"></AdQuillEditor>
            {:else}
            <TranscriptEditorPreview eventIndex="{eventIndex}"></TranscriptEditorPreview>
            {/if}
            <div class="row">
                <EventSpeakers eventIndex="{eventIndex}"></EventSpeakers>
                <EventRecipients eventIndex="{eventIndex}"></EventRecipients>
                <EventTags eventIndex="{eventIndex}"></EventTags>
            </div>
        </div>
    </div>
    {#if $eventGroupState[$projectState.selected].selected[0] === eventIndex}
        <button type="button" class="btn btn-primary btn-sm newEventButton" title="Insert Event" on:click="{insertEvent}"><i class="bi bi-plus-lg"></i></button>
    {/if}
</form>

<style>
.newEventButton{
    box-shadow: 0.3rem 0.3rem 0.3rem rgb(0 0 0 / 50%);
    float: left;
    -webkit-text-stroke: 1px;
}

.eventItem {
    min-height: 229px;
}

.speakerStyleSelect {
    padding: 0;
    background: none;
    border: none;
    border-bottom: 1px solid;
}

select>option {
    color: var(--bs-light);
}

.glowing-button {
    animation: glowing 2000ms infinite;
    box-shadow: 0 0 3px #0fbc8c;
}

@keyframes glowing {
  0% {
    background-color: #0fbc8c;
    box-shadow: 0 0 3px #0fbc8c;
  }
  50% {
    background-color: #11dda3;
    box-shadow: 0 0 10px #11dda3;
  }
  100% {
    background-color: #0de9aa;
    box-shadow: 0 0 3px #0de9aa;
  }
}
</style>
